import React, { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";

/*
 * AmbientParticles
 * ----------------
 * A whisper-quiet field of drifting champagne dots for atmospheric depth.
 * Positioned absolute; parent should be relative.
 * Respects prefers-reduced-motion (renders nothing).
 */
export default function AmbientParticles({
  count = 22,
  className = "",
  seed = 7,
}) {
  const reduced = useReducedMotion();

  const particles = useMemo(() => {
    const rand = (n) => {
      const x = Math.sin(seed * 91 + n) * 10000;
      return x - Math.floor(x);
    };
    return Array.from({ length: count }, (_, i) => {
      const k = i * 13 + 1;
      return {
        x: rand(k) * 100,
        y: rand(k + 1) * 100,
        size: 0.9 + rand(k + 2) * 1.1,
        baseOpacity: 0.16 + rand(k + 3) * 0.22,
        dx: (rand(k + 4) - 0.5) * 40,
        dy: (rand(k + 5) - 0.5) * 24,
        duration: 18 + rand(k + 6) * 16,
        delay: rand(k + 7) * -20,
      };
    });
  }, [count, seed]);

  if (reduced) return null;

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      style={{ zIndex: 0 }}
    >
      {particles.map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: "#F5D896",
            opacity: p.baseOpacity,
            filter: "blur(0.4px)",
          }}
          animate={{
            x: [0, p.dx, -p.dx * 0.7, 0],
            y: [0, p.dy, -p.dy * 0.5, 0],
            opacity: [
              p.baseOpacity,
              p.baseOpacity * 1.6,
              p.baseOpacity * 0.55,
              p.baseOpacity,
            ],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: [0.4, 0, 0.6, 1],
          }}
        />
      ))}
    </div>
  );
}
